import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {Clock, Shield} from 'lucide-react'

interface SessionExpiredModalProps {
  show: boolean
  onLoginRedirect: () => void
}

const SessionExpiredModal: React.FC<SessionExpiredModalProps> = ({show, onLoginRedirect}) => {
  const [countdown, setCountdown] = useState(5)

  useEffect(() => {
    if (!show) {
      setCountdown(5) // Reset countdown when modal is hidden
      return
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Time's up, redirect to login
          onLoginRedirect()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [show, onLoginRedirect])

  const handleClose = () => {
    // Don't allow closing without action - auto redirect will handle it
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop='static'
      keyboard={false}
      centered
      data-testid='session-expired-modal'
    >
      <div
        className='modal-content border-0'
        // style={{
        //   borderRadius: '16px',
        //   background: '#ffffff',
        //   boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        //   maxWidth: '400px',
        //   margin: '0 auto',
        // }}
      >
        {/* Header */}
        <div className='text-center pt-8 pb-6'>
          <div
            className='d-flex align-items-center justify-content-center mx-auto mb-4'
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
              boxShadow: '0 8px 16px rgba(239, 68, 68, 0.3)',
            }}
          >
            <Clock size={32} color='white' />
          </div>

          <h2
            className='fw-bold mb-3'
            style={{
              fontSize: '24px',
              color: '#1f2937',
              margin: '0',
            }}
          >
            Session Expired
          </h2>

          <p
            className='mb-0'
            style={{
              fontSize: '16px',
              color: '#6b7280',
              lineHeight: '1.5',
            }}
          >
            Your session has expired. You will be
            <br />
            redirected to login automatically.
          </p>
        </div>

        {/* Countdown Section */}
        <div className='px-6 pb-6'>
          <div
            className='d-flex align-items-center justify-content-center p-4 mb-4'
            style={{
              background: '#f9fafb',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
            }}
          >
            {/* Progress Circle */}
            <div className='position-relative me-3'>
              <svg width='48' height='48' style={{transform: 'rotate(-90deg)'}}>
                <circle cx='24' cy='24' r='20' fill='none' stroke='#e5e7eb' strokeWidth='3' />
                <circle
                  cx='24'
                  cy='24'
                  r='20'
                  fill='none'
                  stroke='#ef4444'
                  strokeWidth='3'
                  strokeLinecap='round'
                  strokeDasharray={`${2 * Math.PI * 20}`}
                  strokeDashoffset={`${2 * Math.PI * 20 * (1 - countdown / 5)}`}
                  style={{
                    transition: 'stroke-dashoffset 1s ease-in-out',
                  }}
                />
              </svg>
              <div
                className='position-absolute top-50 start-50 translate-middle fw-bold'
                style={{
                  fontSize: '18px',
                  color: '#1f2937',
                }}
              >
                {countdown}
              </div>
            </div>

            <div>
              <div
                className='fw-semibold mb-1'
                style={{
                  fontSize: '16px',
                  color: '#1f2937',
                }}
              >
                Redirecting...
              </div>
              <div
                style={{
                  fontSize: '14px',
                  color: '#6b7280',
                }}
              >
                {countdown} second{countdown !== 1 ? 's' : ''} remaining
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div
            className='d-flex align-items-center justify-content-center p-3'
            style={{
              background: '#fef3f2',
              borderRadius: '8px',
              border: '1px solid #fecaca',
            }}
          >
            <Shield size={16} className='text-danger me-2' />
            <span
              style={{
                fontSize: '13px',
                color: '#991b1b',
                fontWeight: '500',
              }}
            >
              This is for your account security
            </span>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default SessionExpiredModal
